import { AlertTriangle, CheckCircle2, Sparkles, XCircle } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

/**
 * High-level intake recommendation labels used to communicate case disposition likelihood.
 */
type RecommendationOption =
  | 'Likely Accept'
  | 'Potential Accept'
  | 'Uncertain'
  | 'Potential Decline'
  | 'Likely Decline';

/**
 * Aggregated summary and recommendation state for the intake review.
 */
export type SummaryData = {
  caseSummary: string;
  greenFlags: string;
  yellowFlags: string;
  redFlags: string;
  recommendationDecision: RecommendationOption | '';
  recommendationReason: string;
  recommendationCounterpointHeading?: string | null;
  recommendationCounterpoint?: string | null;
};

/**
 * Props for `SummaryAndRecommendationPanel` component.
 */
interface SummaryAndRecommendationPanelProps {
  data: SummaryData;
  onChange: (next: SummaryData) => void;
  isLoading?: boolean;
  onGenerateSummary?: () => void | Promise<void>;
}

/**
 * Displays and edits the case summary, indicator lists, and recommendation.
 */
export const SummaryAndRecommendationPanel: React.FC<
  SummaryAndRecommendationPanelProps
> = ({ data, onChange, isLoading, onGenerateSummary }) => {
  const [isEditingSummary, setIsEditingSummary] =
    React.useState<boolean>(false);

  const [isEditingIndicators, setIsEditingIndicators] =
    React.useState<boolean>(false);

  const [isEditingRecommendation, setIsEditingRecommendation] =
    React.useState<boolean>(false);

  /**
   * Curried field updater for `SummaryData` keys.
   */
  const handleField =
    <K extends keyof SummaryData>(key: K) =>
    (value: SummaryData[K]) => {
      onChange({ ...data, [key]: value });
    };

  /**
   * Converts a comma-separated string into a trimmed list, removing empties.
   */
  const splitList = (value?: string | null): ReadonlyArray<string> => {
    const source = typeof value === 'string' ? value : '';
    return source
      .split(',')
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  const positiveItems = splitList(data.greenFlags);
  const cautionItems = splitList(data.yellowFlags);
  const warningItems = splitList(data.redFlags);

  /**
   * Derives a sensible default counterpoint heading from the selected decision.
   */
  const getCounterpointHeading = (
    decision: RecommendationOption | '',
  ): string | null => {
    if (decision === 'Likely Accept' || decision === 'Potential Accept') {
      return 'Why you might not take the case:';
    }
    if (decision === 'Uncertain') {
      return null;
    }
    return 'Why you would take the case:';
  };

  const derivedHeading = getCounterpointHeading(data.recommendationDecision);
  const counterpointHeading =
    data.recommendationCounterpointHeading ?? derivedHeading ?? '';

  /**
   * Persists the explicit counterpoint heading selection. Empty string indicates no heading.
   */
  const handleCounterpointHeadingChange = (value: string) => {
    // Persist empty string to explicitly mean "no heading"
    onChange({ ...data, recommendationCounterpointHeading: value });
  };

  return (
    <div
      className="relative bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden min-h-[680px]"
      aria-busy={!!isLoading}
    >
      {/* Loading overlay */}
      {isLoading ? (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/70 backdrop-blur-[1px]">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
          <div className="mt-2 text-sm text-gray-700">Generating summary…</div>
        </div>
      ) : null}

      <div className="px-5 pt-4 pb-2 border-b border-gray-100 flex items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-gray-900">
          Case Summary & Recommendation
        </h2>
        {onGenerateSummary && (
          <Button
            onClick={() => onGenerateSummary()}
            size="sm"
            variant="outline"
            className="text-xs px-3 py-1.5 gap-1.5"
            disabled={!!isLoading}
          >
            <Sparkles className="h-3.5 w-3.5" />
            {isLoading ? 'Generating…' : 'AI Summary'}
          </Button>
        )}
      </div>

      <div className="p-5 space-y-6">
        {/* Summary */}
        <section aria-label="Case summary" className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="case-summary" className="text-sm font-semibold">
              Summary
            </Label>
            <Button
              onClick={() => setIsEditingSummary((v) => !v)}
              size="sm"
              variant="outline"
              className="text-xs px-3 py-1.5"
              aria-label={
                isEditingSummary ? 'Done editing summary' : 'Edit summary'
              }
              disabled={!!isLoading}
            >
              {isEditingSummary ? 'Done' : 'Edit summary'}
            </Button>
          </div>
          {isEditingSummary ? (
            <Textarea
              id="case-summary"
              rows={6}
              value={data.caseSummary}
              onChange={(e) => handleField('caseSummary')(e.target.value)}
              placeholder="High-level narrative summary (about a paragraph)"
              className="mt-1"
              aria-label="Edit case summary"
              disabled={!!isLoading}
            />
          ) : (
            <div className="rounded-md border border-gray-100 bg-gray-50 p-4 text-gray-800 leading-7">
              {data.caseSummary || (
                <span className="text-gray-400">
                  No summary yet. Click Edit to add one.
                </span>
              )}
            </div>
          )}
        </section>

        {/* Indicators */}
        <section aria-label="Indicators" className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Indicators</Label>
            <Button
              onClick={() => setIsEditingIndicators((v) => !v)}
              size="sm"
              variant="outline"
              className="text-xs px-3 py-1.5"
              aria-label={
                isEditingIndicators
                  ? 'Done editing indicators'
                  : 'Edit indicators'
              }
              disabled={!!isLoading}
            >
              {isEditingIndicators ? 'Done' : 'Edit indicators'}
            </Button>
          </div>

          {isEditingIndicators ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label
                  htmlFor="green-flags"
                  className="text-xs font-medium text-gray-700"
                >
                  Positive Indicators (comma-separated)
                </Label>
                <Textarea
                  id="green-flags"
                  rows={3}
                  value={data.greenFlags}
                  onChange={(e) => handleField('greenFlags')(e.target.value)}
                  placeholder="Clear liability, Adverse admission, ..."
                  className="mt-1"
                  aria-label="Edit positive indicators"
                  disabled={!!isLoading}
                />
              </div>
              <div>
                <Label
                  htmlFor="yellow-flags"
                  className="text-xs font-medium text-gray-700"
                >
                  Caution Indicators (comma-separated)
                </Label>
                <Textarea
                  id="yellow-flags"
                  rows={3}
                  value={data.yellowFlags}
                  onChange={(e) => handleField('yellowFlags')(e.target.value)}
                  placeholder="Limited injuries, Delayed treatment, ..."
                  className="mt-1"
                  aria-label="Edit caution indicators"
                  disabled={!!isLoading}
                />
              </div>
              <div>
                <Label
                  htmlFor="red-flags"
                  className="text-xs font-medium text-gray-700"
                >
                  Warning Indicators (comma-separated)
                </Label>
                <Textarea
                  id="red-flags"
                  rows={3}
                  value={data.redFlags}
                  onChange={(e) => handleField('redFlags')(e.target.value)}
                  placeholder="Coverage denied, Fraud concerns, ..."
                  className="mt-1"
                  aria-label="Edit warning indicators"
                  disabled={!!isLoading}
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center gap-2">
                  <CheckCircle2
                    className="h-5 w-5 text-emerald-600"
                    aria-hidden="true"
                  />
                  <span className="text-sm font-semibold text-gray-900">
                    Positive Indicators
                  </span>
                </div>
                <ul className="mt-2 list-disc list-inside text-sm text-gray-700 space-y-1">
                  {positiveItems.length > 0 ? (
                    positiveItems.map((item, idx) => <li key={idx}>{item}</li>)
                  ) : (
                    <li className="text-gray-400">None</li>
                  )}
                </ul>
              </div>
              <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center gap-2">
                  <AlertTriangle
                    className="h-5 w-5 text-amber-600"
                    aria-hidden="true"
                  />
                  <span className="text-sm font-semibold text-gray-900">
                    Caution Indicators
                  </span>
                </div>
                <ul className="mt-2 list-disc list-inside text-sm text-gray-700 space-y-1">
                  {cautionItems.length > 0 ? (
                    cautionItems.map((item, idx) => <li key={idx}>{item}</li>)
                  ) : (
                    <li className="text-gray-400">None</li>
                  )}
                </ul>
              </div>
              <div className="rounded-md border border-gray-100 bg-gray-50 p-4">
                <div className="flex items-center gap-2">
                  <XCircle
                    className="h-5 w-5 text-rose-600"
                    aria-hidden="true"
                  />
                  <span className="text-sm font-semibold text-gray-900">
                    Warning Indicators
                  </span>
                </div>
                <ul className="mt-2 list-disc list-inside text-sm text-gray-700 space-y-1">
                  {warningItems.length > 0 ? (
                    warningItems.map((item, idx) => <li key={idx}>{item}</li>)
                  ) : (
                    <li className="text-gray-400">None</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </section>

        {/* Recommendation */}
        <section aria-label="Recommendation" className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Recommendation</Label>
            <Button
              onClick={() => setIsEditingRecommendation((v) => !v)}
              size="sm"
              variant="outline"
              className="text-xs px-3 py-1.5"
              aria-label={
                isEditingRecommendation
                  ? 'Done editing recommendation'
                  : 'Edit recommendation'
              }
              disabled={!!isLoading}
            >
              {isEditingRecommendation ? 'Done' : 'Edit recommendation'}
            </Button>
          </div>

          <div className="rounded-md border border-gray-200">
            <div className="p-4 border-b border-gray-100 bg-gray-50">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div className="text-sm font-semibold text-gray-900">
                  {data.recommendationDecision || '—'}
                </div>
                {isEditingRecommendation && (
                  <div className="md:w-80">
                    <Select
                      value={data.recommendationDecision}
                      onValueChange={(v) =>
                        handleField('recommendationDecision')(
                          v as RecommendationOption | '',
                        )
                      }
                      disabled={!!isLoading}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a recommendation" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Likely Accept">
                          Likely Accept
                        </SelectItem>
                        <SelectItem value="Potential Accept">
                          Potential Accept
                        </SelectItem>
                        <SelectItem value="Uncertain">Uncertain</SelectItem>
                        <SelectItem value="Potential Decline">
                          Potential Decline
                        </SelectItem>
                        <SelectItem value="Likely Decline">
                          Likely Decline
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 space-y-3">
              {isEditingRecommendation ? (
                <>
                  <Label
                    htmlFor="recommendation-heading"
                    className="text-xs font-medium text-gray-700"
                  >
                    Counterpoint heading
                  </Label>
                  <div className="mb-3">
                    <Select
                      value={
                        counterpointHeading === ''
                          ? 'none'
                          : counterpointHeading
                      }
                      onValueChange={(v) =>
                        handleCounterpointHeadingChange(v === 'none' ? '' : v)
                      }
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="No heading" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="Why you would take the case:">
                          Why you would take the case:
                        </SelectItem>
                        <SelectItem value="Why you might not take the case:">
                          Why you might not take the case:
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Label
                    htmlFor="recommendation-reason"
                    className="text-xs font-medium text-gray-700"
                  >
                    Reason (two sentences)
                  </Label>
                  <Textarea
                    id="recommendation-reason"
                    rows={3}
                    value={data.recommendationReason}
                    onChange={(e) =>
                      handleField('recommendationReason')(e.target.value)
                    }
                    placeholder="Two-sentence reasoning (liability, treatment, coverage factors)"
                    aria-label="Edit recommendation reason"
                    disabled={!!isLoading}
                  />
                </>
              ) : (
                <div className="text-sm text-gray-800 leading-7">
                  {data.recommendationReason || (
                    <span className="text-gray-400">
                      No reasoning provided yet.
                    </span>
                  )}
                </div>
              )}

              <div className="pt-2">
                {counterpointHeading ? (
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {counterpointHeading}
                  </div>
                ) : null}
                {isEditingRecommendation ? (
                  <Textarea
                    id="counterpoint"
                    rows={2}
                    value={data.recommendationCounterpoint || ''}
                    onChange={(e) =>
                      handleField('recommendationCounterpoint')(e.target.value)
                    }
                    placeholder="Single-sentence strongest opposing viewpoint"
                    className="mt-1"
                    aria-label="Edit counterpoint"
                    disabled={!!isLoading}
                  />
                ) : (
                  <div className="mt-1 text-sm italic text-gray-700">
                    {data.recommendationCounterpoint || (
                      <span className="not-italic text-gray-400">None</span>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default SummaryAndRecommendationPanel;
